import { useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';

// Import constants

// Import store
import { useScheduleStore, type ScheduleStore } from '../../store/scheduleStore';

// Import utils
import { logger } from '../../utils/logger';

/**
 * Manages participants list, current user selection, ownership by this device (selfNames),
 * join input, and the mapping of events per user.
 * Extracted from SchedulePage/ParticipantsPanel to keep behavior identical.
 */
export function useParticipants(
  deviceId: string,
  ownsParticipant: (ownerDeviceId: string | undefined) => boolean,
  maxParticipants: number,
) {
  const effectiveMaxParticipants = maxParticipants;
  // Bind to Zustand store
  const users = useScheduleStore((s: ScheduleStore) => s.users);
  const setUsers = useScheduleStore((s: ScheduleStore) => s.setUsers);
  const currentUser = useScheduleStore((s: ScheduleStore) => s.currentUser);
  const setCurrentUser = useScheduleStore(
    (s: ScheduleStore) => s.setCurrentUser,
  );
  const joinName = useScheduleStore((s: ScheduleStore) => s.joinName);
  const setJoinName = useScheduleStore((s: ScheduleStore) => s.setJoinName);
  const userIdToName = useScheduleStore((s: ScheduleStore) => s.userIdToName);
  const setUserIdToName = useScheduleStore(
    (s: ScheduleStore) => s.setUserIdToName,
  );
  const ownerByUserId = useScheduleStore((s: ScheduleStore) => s.ownerByUserId);
  const setOwnerByUserId = useScheduleStore(
    (s: ScheduleStore) => s.setOwnerByUserId,
  );
  const eventsByUser = useScheduleStore((s: ScheduleStore) => s.eventsByUser);
  const setEventsByUser = useScheduleStore(
    (s: ScheduleStore) => s.setEventsByUser,
  );

  // Derived names owned by this device (for UI copy only)
  const selfNames = useMemo(() => {
    const owned: string[] = [];
    for (const id of users) {
      if (ownsParticipant(ownerByUserId[id])) owned.push(userIdToName[id] || id);
    }
    return owned;
  }, [users, userIdToName, ownerByUserId, ownsParticipant]);

  const handleRemoveUser = useCallback(
    (userId: string) => {
      logger.log('REMOVE USER - userId:', userId);

      // Remove a user from the user's list
      const nextUsers = users.filter((x: string) => x !== userId);

      // Remove user from eventsByUser
      const nextEvents: typeof eventsByUser = { ...eventsByUser };
      delete nextEvents[userId];

      // Remove user from userIdToName
      const nextNames = { ...userIdToName };
      delete nextNames[userId];

      // Remove user from ownerByUserId
      const nextOwners = { ...ownerByUserId };
      delete nextOwners[userId];

      // Set the current user to another user if needed
      let nextCurrent = currentUser;
      if (currentUser === userId) {
        // Prefer another owned user if any, else first remaining
        const ownedIds = nextUsers.filter(
          (id: string) => nextOwners[id] === deviceId,
        );
        if (ownedIds.length) nextCurrent = ownedIds[0];
        else nextCurrent = nextUsers[0] || '';
      }

      logger.log('REMOVE USER - nextUsers:', nextUsers);
      logger.log('REMOVE USER - nextEvents:', nextEvents);
      logger.log('REMOVE USER - nextNames:', nextNames);
      logger.log('REMOVE USER - nextOwners:', nextOwners);

      // Update state
      setUsers(nextUsers);
      setEventsByUser(nextEvents);
      setUserIdToName(nextNames);
      setOwnerByUserId(nextOwners);
      setCurrentUser(nextCurrent);
    },
    [
      currentUser,
      deviceId,
      eventsByUser,
      ownerByUserId,
      setCurrentUser,
      setEventsByUser,
      setOwnerByUserId,
      setUserIdToName,
      setUsers,
      userIdToName,
      users,
    ],
  );

  const handleJoin = useCallback(() => {
    const name = joinName.trim();

    if (!name) {
      toast.error('Please enter your name');
      return;
    }

    if (users.length >= effectiveMaxParticipants) {
      toast.error(
        `This meeting planner has the maximum of ${effectiveMaxParticipants} participants`,
      );
      return;
    }

    // Create a new user and add them to the list
    const id = crypto.randomUUID();
    setUsers([...users, id]);
    setEventsByUser({ ...eventsByUser, [id]: [] });
    setUserIdToName({ ...userIdToName, [id]: name });
    setOwnerByUserId({ ...ownerByUserId, [id]: deviceId });
    setCurrentUser(id);
    setJoinName('');
  }, [joinName, users, effectiveMaxParticipants, setUsers, setEventsByUser, eventsByUser, setUserIdToName, userIdToName, setOwnerByUserId, ownerByUserId, deviceId, setCurrentUser, setJoinName]);

  const handleRenameUser = useCallback(
    (userId: string, newNameRaw: string) => {
      const newName = newNameRaw.trim();
      // ignore empty strings
      if (!newName) {
        toast.error('Please enter a valid name');
        return;
      }

      // Only allow renaming users created on this device (including linked devices)
      const owner = ownerByUserId[userId];
      if (!ownsParticipant(owner)) {
        toast.error('You can only rename users you created on this device or linked devices');
        return;
      }

      setUserIdToName({ ...userIdToName, [userId]: newName });
    },
    [ownerByUserId, ownsParticipant, userIdToName, setUserIdToName],
  );

  return {
    // state
    users,
    currentUser,
    selfNames, // derived, UI-only convenience
    joinName,
    eventsByUser,
    userIdToName,
    ownerByUserId,
    // core setters (needed for persistence hydration)
    setUsers,
    setUserIdToName,
    setOwnerByUserId,
    // setters where useful for UI bindings
    setJoinName,
    setCurrentUser,
    setEventsByUser,
    // actions
    handleRemoveUser,
    handleJoin,
    handleRenameUser,
  };
}

export default useParticipants;
